import { useState, type ReactNode } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Inbox, Check, X, Umbrella, CalendarDays, Clock, Undo2, CalendarRange, CheckCircle2, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import {
  usePendingLeaves, useApproveLeave, useRejectLeave,
  usePendingTimeAdjustments, useApproveTimeAdjustment, useRejectTimeAdjustment,
  useApprovedLeaves, useCancelLeave,
  useApprovedTimeAdjustments, useCancelTimeAdjustment,
  usePharmacyNotifications,
  type PendingLeave, type PendingTimeAdjustment,
} from '@/api/resources';
import { absenceLabel } from '@/lib/absenceTypes';
import { RequestWeekPreview, type PreviewFocus } from './RequestWeekPreview';
import VariableFillModal from './VariableFillModal';
import type { VariableStaffMember } from '@/types';

const fmtDay = (iso: string) => format(new Date(iso), 'EEEE d MMMM', { locale: fr });
const fmtShort = (iso: string) => format(new Date(iso), 'd MMM', { locale: fr });

type Tab = 'pending' | 'approved';

export default function RequestsPage() {
  const [tab, setTab] = useState<Tab>('pending');

  return (
    <div className="flex flex-col h-full">
      <header className="px-8 py-5 border-b border-slate-200">
        <div className="flex items-center gap-2.5">
          <Inbox size={20} className="text-primary" />
          <h1 className="text-[22px] font-semibold tracking-tight">Demandes <PageHelp topic="requests" /></h1>
        </div>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          Congés et corrections d'horaire proposés par vos salariés depuis l'application mobile.
        </p>
        <div className="mt-4 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>
            En attente
          </TabButton>
          <TabButton active={tab === 'approved'} onClick={() => setTab('approved')}>
            Acceptées
          </TabButton>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8 max-w-3xl">
        {tab === 'pending' ? <PendingView /> : <ApprovedView />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
        active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

// ============================================================================
// Vue « En attente » — accepter / refuser
// ============================================================================

function PendingView() {
  const leaves = usePendingLeaves();
  const adjustments = usePendingTimeAdjustments();
  // Qui n'a pas encore confirmé avoir vu son planning recalculé ?
  const acks = usePharmacyNotifications(true);

  const approveLeave = useApproveLeave();
  const rejectLeave = useRejectLeave();
  const approveAdj = useApproveTimeAdjustment();
  const rejectAdj = useRejectTimeAdjustment();

  const [preview, setPreview] = useState<PreviewFocus | null>(null);

  // Après validation d'une absence, on PROPOSE de recalculer les horaires du
  // personnel en horaires variables (jamais d'office : c'est le titulaire qui
  // choisit qui recalculer).
  const [recompute, setRecompute] = useState<{
    from: string;
    to: string;
    staff: VariableStaffMember[];
    reason: string;
  } | null>(null);

  async function handleApproveLeave(l: PendingLeave) {
    const res = await approveLeave.mutateAsync(l.id);
    if (res.suggestVariableRecompute) {
      setRecompute({
        from: res.recomputeRange.from.slice(0, 10),
        to: res.recomputeRange.to.slice(0, 10),
        staff: res.variableStaff,
        reason: `Absence de ${l.employee.firstName} ${l.employee.lastName} validée ${fmtShort(l.startDate)} → ${fmtShort(l.endDate)}.`,
      });
    }
  }

  const leaveCount = leaves.data?.length ?? 0;
  const adjCount = adjustments.data?.length ?? 0;

  return (
    <>
      <section>
        <SectionTitle icon={<Umbrella size={16} />} label="Congés" count={leaveCount} />
        {leaves.isLoading ? (
          <LoadingRow />
        ) : leaveCount === 0 ? (
          <EmptyRow done label="Aucune demande de congé en attente." hint="Vous êtes à jour — rien à valider pour le moment." />
        ) : (
          <div className="space-y-3">
            {leaves.data!.map((l) => (
              <LeaveDecisionCard
                key={l.id}
                leave={l}
                onApprove={() => handleApproveLeave(l)}
                onReject={(note) => rejectLeave.mutate({ id: l.id, reviewNote: note })}
                onPreview={() => setPreview({
                  kind: 'leave', id: l.id, employeeId: l.employeeId,
                  employeeName: `${l.employee.firstName} ${l.employee.lastName}`,
                  date: l.startDate.slice(0, 10),
                  startDate: l.startDate.slice(0, 10), endDate: l.endDate.slice(0, 10),
                })}
                approving={approveLeave.isPending && approveLeave.variables === l.id}
                rejecting={rejectLeave.isPending && rejectLeave.variables?.id === l.id}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionTitle icon={<Clock size={16} />} label="Corrections d'horaire" count={adjCount} />
        {adjustments.isLoading ? (
          <LoadingRow />
        ) : adjCount === 0 ? (
          <EmptyRow done label="Aucune correction d'horaire en attente." hint="Les heures déclarées par l'équipe correspondent au planning." />
        ) : (
          <div className="space-y-3">
            {adjustments.data!.map((a) => (
              <AdjustmentDecisionCard
                key={a.id}
                adj={a}
                onApprove={() => approveAdj.mutate(a.id)}
                onReject={(note) => rejectAdj.mutate({ id: a.id, reviewNote: note })}
                onPreview={() => setPreview({
                  kind: 'adjustment', id: a.id, employeeId: a.employeeId,
                  employeeName: `${a.employee.firstName} ${a.employee.lastName}`,
                  date: a.date.slice(0, 10),
                })}
                approving={approveAdj.isPending && approveAdj.variables === a.id}
                rejecting={rejectAdj.isPending && rejectAdj.variables?.id === a.id}
              />
            ))}
          </div>
        )}
      </section>

      {/* Accusés de réception : purement informatif, aucune décision à prendre. */}
      {(acks.data?.pendingAckCount ?? 0) > 0 && (
        <section>
          <SectionTitle
            icon={<BellRing size={16} />}
            label="Accusés de réception en attente"
            count={acks.data!.pendingAckCount}
          />
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {acks.data!.notifications.slice(0, 8).map((n) => (
              <div key={n.id} className="px-4 py-3 flex items-start gap-3">
                <span
                  className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: n.employee?.color ?? '#94a3b8' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-slate-900">
                    {n.employee ? `${n.employee.firstName} ${n.employee.lastName}` : 'Salarié'}
                  </div>
                  <div className="text-[11.5px] text-slate-500 mt-0.5">{n.message}</div>
                </div>
                <span className="text-[10.5px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                  pas encore vu
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Ces salariés ont reçu un planning recalculé sur leur application mobile mais
            ne l'ont pas encore confirmé.
          </p>
        </section>
      )}

      {preview && <RequestWeekPreview focus={preview} onClose={() => setPreview(null)} />}

      {recompute && (
        <VariableFillModal
          from={recompute.from}
          to={recompute.to}
          staff={recompute.staff}
          reason={recompute.reason}
          onClose={() => setRecompute(null)}
        />
      )}
    </>
  );
}

// ============================================================================
// Vue « Acceptées » — annuler / révoquer
// ============================================================================

function ApprovedView() {
  const leaves = useApprovedLeaves();
  const adjustments = useApprovedTimeAdjustments();

  const cancelLeave = useCancelLeave();
  const cancelAdj = useCancelTimeAdjustment();

  const leaveCount = leaves.data?.length ?? 0;
  const adjCount = adjustments.data?.length ?? 0;

  return (
    <>
      <section>
        <SectionTitle icon={<Umbrella size={16} />} label="Congés acceptés" count={leaveCount} />
        {leaves.isLoading ? (
          <LoadingRow />
        ) : leaveCount === 0 ? (
          <EmptyRow label="Aucun congé accepté." hint="Les congés que vous validez apparaîtront ici." />
        ) : (
          <div className="space-y-3">
            {leaves.data!.map((l) => (
              <CancelCard
                key={l.id}
                header={`${l.employee.firstName} ${l.employee.lastName}`}
                meta={<LeaveMeta leave={l} />}
                body={l.reason ? `« ${l.reason} »` : undefined}
                onCancel={() => cancelLeave.mutate(l.id)}
                cancelling={cancelLeave.isPending && cancelLeave.variables === l.id}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionTitle icon={<Clock size={16} />} label="Corrections d'horaire acceptées" count={adjCount} />
        {adjustments.isLoading ? (
          <LoadingRow />
        ) : adjCount === 0 ? (
          <EmptyRow label="Aucune correction acceptée." hint="Les corrections que vous validez apparaîtront ici." />
        ) : (
          <div className="space-y-3">
            {adjustments.data!.map((a) => (
              <CancelCard
                key={a.id}
                header={`${a.employee.firstName} ${a.employee.lastName}`}
                meta={<AdjustmentMeta adj={a} />}
                body={a.reason ? `« ${a.reason} »` : undefined}
                onCancel={() => cancelAdj.mutate(a.id)}
                cancelling={cancelAdj.isPending && cancelAdj.variables === a.id}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

// ============================================================================
// Sous-composants partagés
// ============================================================================

function SectionTitle({ icon, label, count }: { icon: ReactNode; label: string; count: number }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-slate-500">{icon}</span>
      <h2 className="text-[15px] font-semibold text-slate-900">{label}</h2>
      {count > 0 && <Chip variant="amber" size="sm">{count}</Chip>}
    </div>
  );
}

function LoadingRow() {
  return <div className="text-[13px] text-slate-400 py-6">Chargement…</div>;
}

/**
 * État vide illustré (demande du boss : une file vide ressemblait à un bug de chargement).
 * `done` = « rien à traiter, c'est normal » → ton positif ; sinon, simple info neutre.
 */
function EmptyRow({ label, hint, done = false }: { label: string; hint?: string; done?: boolean }) {
  const Icon = done ? CheckCircle2 : Inbox;
  return (
    <div className="py-10 text-center border border-dashed border-slate-200 rounded-xl">
      <div
        className={`w-11 h-11 rounded-full mx-auto mb-3 flex items-center justify-center ${
          done ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
        }`}
      >
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className={`text-[13px] font-medium ${done ? 'text-emerald-800' : 'text-slate-600'}`}>{label}</div>
      {hint && <div className="text-[11.5px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function LeaveMeta({ leave }: { leave: PendingLeave }) {
  const days = Number.isInteger(leave.daysCount) ? leave.daysCount : leave.daysCount.toFixed(1);
  return (
    <span className="inline-flex items-center gap-1.5">
      <CalendarDays size={12} />
      Du {fmtShort(leave.startDate)} au {fmtShort(leave.endDate)} · {days} j · {absenceLabel(leave.type)}
    </span>
  );
}

function AdjustmentMeta({ adj }: { adj: PendingTimeAdjustment }) {
  const slots = [
    adj.morningStart && adj.morningEnd ? `${adj.morningStart} – ${adj.morningEnd}` : null,
    adj.afternoonStart && adj.afternoonEnd ? `${adj.afternoonStart} – ${adj.afternoonEnd}` : null,
  ].filter(Boolean).join('  ·  ');
  return (
    <span className="inline-flex items-center gap-1.5">
      <Clock size={12} />
      {fmtDay(adj.date)} — horaires réels : {slots || '—'}
    </span>
  );
}

/** Carte générique avec actions accepter / refuser (motif). */
function DecisionCard({
  header, meta, body, onApprove, onReject, onPreview, approving, rejecting,
}: {
  header: ReactNode;
  meta: ReactNode;
  body?: ReactNode;
  onApprove: () => void;
  onReject: (note: string) => void;
  onPreview: () => void;
  approving: boolean;
  rejecting: boolean;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [note, setNote] = useState('');
  const busy = approving || rejecting;

  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onPreview}
          title="Voir l'effet sur la semaine"
          className="min-w-0 text-left group"
        >
          <div className="text-[14px] font-semibold text-slate-900 group-hover:text-primary">{header}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">{meta}</div>
          {body && <div className="text-[12.5px] text-slate-700 mt-2">{body}</div>}
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-primary opacity-70 group-hover:opacity-100">
            <CalendarRange size={12} /> Voir la semaine
          </span>
        </button>
        {!rejectOpen && (
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="default" size="sm" icon={X} disabled={busy} onClick={() => setRejectOpen(true)}>
              Refuser
            </Button>
            <Button variant="primary" size="sm" icon={Check} loading={approving} disabled={busy} onClick={onApprove}>
              Accepter
            </Button>
          </div>
        )}
      </div>

      {rejectOpen && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <label className="text-[12px] font-medium text-slate-600">Motif du refus</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            autoFocus
            placeholder="Expliquez pourquoi la demande est refusée…"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:border-primary focus:outline-none"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => { setRejectOpen(false); setNote(''); }}>
              Annuler
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={rejecting}
              disabled={busy || note.trim().length === 0}
              onClick={() => onReject(note.trim())}
            >
              Confirmer le refus
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Carte d'une demande acceptée avec action « Annuler » (confirmation inline). */
function CancelCard({
  header, meta, body, onCancel, cancelling,
}: {
  header: ReactNode;
  meta: ReactNode;
  body?: ReactNode;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-slate-900">{header}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">{meta}</div>
          {body && <div className="text-[12.5px] text-slate-700 mt-2">{body}</div>}
        </div>
        {!confirmOpen && (
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="default" size="sm" icon={Undo2} disabled={cancelling} onClick={() => setConfirmOpen(true)}>
              Annuler
            </Button>
          </div>
        )}
      </div>

      {confirmOpen && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="text-[12.5px] text-slate-600">
            Annuler cette demande acceptée&nbsp;? L'effet sur le planning sera défait.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" size="sm" disabled={cancelling} onClick={() => setConfirmOpen(false)}>
              Retour
            </Button>
            <Button variant="danger" size="sm" loading={cancelling} disabled={cancelling} onClick={onCancel}>
              Confirmer l'annulation
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LeaveDecisionCard({
  leave, onApprove, onReject, onPreview, approving, rejecting,
}: {
  leave: PendingLeave;
  onApprove: () => void;
  onReject: (note: string) => void;
  onPreview: () => void;
  approving: boolean;
  rejecting: boolean;
}) {
  return (
    <DecisionCard
      header={`${leave.employee.firstName} ${leave.employee.lastName}`}
      meta={<LeaveMeta leave={leave} />}
      body={leave.reason ? `« ${leave.reason} »` : undefined}
      onApprove={onApprove}
      onReject={onReject}
      onPreview={onPreview}
      approving={approving}
      rejecting={rejecting}
    />
  );
}

function AdjustmentDecisionCard({
  adj, onApprove, onReject, onPreview, approving, rejecting,
}: {
  adj: PendingTimeAdjustment;
  onApprove: () => void;
  onReject: (note: string) => void;
  onPreview: () => void;
  approving: boolean;
  rejecting: boolean;
}) {
  return (
    <DecisionCard
      header={`${adj.employee.firstName} ${adj.employee.lastName}`}
      meta={<AdjustmentMeta adj={adj} />}
      body={adj.reason ? `« ${adj.reason} »` : undefined}
      onApprove={onApprove}
      onReject={onReject}
      onPreview={onPreview}
      approving={approving}
      rejecting={rejecting}
    />
  );
}
